import { v4 as uuid4 } from 'uuid';
import { LOCAL_STORAGE_KEYS } from '../consts/localStorage';

/**
 * Read and remove the post-login redirect target from localStorage.
 *
 * Multiple flows seed `LOCAL_STORAGE_KEYS.REDIRECT_TO` (the `?redirectTo=` URL
 * query param captured by `Login`, the GitHub Marketplace callback, etc.).
 * After a successful sign-in the destination is consumed exactly once,
 * regardless of which sign-in pathway completes (token-handoff or the
 * Firebase auth-state listener). Tolerates both JSON-encoded and raw string
 * values to stay compatible with all writers, and always returns a
 * router-friendly path prefixed with `/`.
 *
 * @returns The redirect path, or `undefined` if no entry was present.
 */
export const consumeAndRemoveRedirectToFromLocalStorage = ():
  | string
  | undefined => {
  const raw = localStorage.getItem(LOCAL_STORAGE_KEYS.REDIRECT_TO);

  if (!raw) {
    return undefined;
  }

  localStorage.removeItem(LOCAL_STORAGE_KEYS.REDIRECT_TO);

  let value = raw;

  try {
    const decoded = JSON.parse(raw);

    if (typeof decoded === 'string') {
      value = decoded;
    }
  } catch {
    console.warn(
      'raw was not JSON-encoded — treat the original string as the path.',
    );
  }

  if (!value) {
    return undefined;
  }

  return value.startsWith('/') ? value : `/${value}`;
};

/**
 * Retrieves a value from localStorage by key; if absent, generates a UUID v4, stores it under the key, and returns it.
 * @param key - The localStorage key to read/write.
 * @returns The existing or newly generated UUID string.
 */
export const getOrGenerateValueFromLocalStorage = (key: string): string => {
  const value = localStorage.getItem(key);

  if (!value) {
    const generatedValue = uuid4();

    localStorage.setItem(key, generatedValue);

    return generatedValue;
  }

  return value;
};

/**
 * Factory that creates typed localStorage state utilities with TTL support.
 * Handles store / get / validate (TTL) / clear operations with a single localStorage key.
 *
 * @param localStorageKey - The localStorage key to use
 * @param ttlMs - Time-to-live in milliseconds; validate() returns false after this age
 *
 * @example
 * const azureState = createLocalStorageStateUtils<ConsentState>(
 *   LOCAL_STORAGE_KEYS.AZURE_INSTALLATION_STATE,
 *   30 * 60 * 1000,
 * );
 * azureState.store(state);
 * azureState.validate(); // true if stored and < ttlMs old
 * azureState.clear();
 */
export function createLocalStorageStateUtils<T extends { timestamp: number }>(
  localStorageKey: string,
  ttlMs: number,
) {
  const store = (state: T): void => {
    localStorage.setItem(localStorageKey, JSON.stringify(state));
  };

  const get = (): T | undefined => {
    try {
      const stored = localStorage.getItem(localStorageKey);

      if (!stored) return undefined;

      return JSON.parse(stored) as T;
    } catch (error) {
      console.warn('Failed to parse localStorage state:', error);

      return undefined;
    }
  };

  const validate = (): boolean => {
    const state = get();

    if (!state) return false;

    return Date.now() - state.timestamp < ttlMs;
  };

  const clear = (): void => {
    localStorage.removeItem(localStorageKey);
  };

  return { store, get, validate, clear };
}
